import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  CircularProgress,
  TablePagination,
  Tabs,
  Tab,
} from "@mui/material";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import { useSelector } from "react-redux";
import { useOfflineRc } from "../../../offline";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import ExtractedLineItemsModal from "./ExtractedLineItemsModal";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import { enterprisePrimaryButtonSx } from "../../../components/common/upload/enterpriseUploadStyles";

const RcListView = ({
  onAddRc,
  onExtractData,
  onViewPo,
  savedRc,
  activeTab,
  onActiveTabChange,
}) => {
  const loginData = useSelector((state) => state.loginData.value);
  const auth_token = loginData?.auth_token;
  const buyer_token = loginData?.user?.token;

  const [rcList, setRcList] = useState([]);
  const [, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [extractedSearch, setExtractedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedExtractedRecord, setSelectedExtractedRecord] = useState(null);
  const [lineItemsOpen, setLineItemsOpen] = useState(false);
  const { rcs, loading: loadingRc } = useOfflineRc(auth_token, buyer_token);
  const [loadingPoId, setLoadingPoId] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const stickyStartHeaderSx = {
    position: "sticky",
    left: 0,
    zIndex: 4,
    backgroundColor: "#e0e9ee",
    boxShadow: "2px 0 0 0 #e2e8f0 inset",
  };

  const stickyEndHeaderSx = {
    position: "sticky",
    right: 0,
    zIndex: 4,
    backgroundColor: "#e0e9ee",
    boxShadow: "-2px 0 0 0 #e2e8f0 inset",
    textAlign: "center",
  };

  const stickyStartCellSx = {
    position: "sticky",
    left: 0,
    zIndex: 3,
    backgroundColor: "#f8fafc",
    boxShadow: "2px 0 0 0 #e2e8f0 inset",
  };

  const stickyEndCellSx = {
    position: "sticky",
    right: 0,
    zIndex: 3,
    backgroundColor: "#f8fafc",
    boxShadow: "-2px 0 0 0 #e2e8f0 inset",
    textAlign: "center",
  };

  const getVendorName = (record) => {
    const vendorRaw = record?.vendor_details || record?.header_info?.vendor_details || "";
    const firstLine = vendorRaw.toString().split("\n")[0]?.trim();
    return firstLine || "-";
  };

  const isPoAutomationRecord = (record) => {
    const explicitTag =
      record?.generated_for === "po_automation" ||
      record?.header_info?.generated_for === "po_automation";
    if (explicitTag) return true;

    // Backward compatibility: older rows may not have `generated_for`.
    // If source order number itself is an RC code, treat it as RC-filter automation.
    const sourceCode = (
      record?.purchase_order_number ||
      record?.header_info?.purchase_order_no ||
      ""
    )
      .toString()
      .trim();
    return /^RC[\/-]/i.test(sourceCode);
  };

  useEffect(() => {
    setRcList(rcs);
  }, [rcs]);

  useEffect(() => {
    if (savedRc) {
      setRcList((prev) => {
        const exists = prev.some((rc) => rc.rc_token === savedRc.rc_token);
        return exists ? prev : [savedRc, ...prev];
      });
    }
  }, [savedRc]);

  const handleCreatePO = useCallback(
    async (rcItem) => {
      try {
        const token = rcItem.rc_token || rcItem.token;
        setLoadingPoId(token);
        setLoading(true);
        onViewPo(rcItem);
      } catch (err) {
        console.error("GET RC ITEMS ERROR:", err);
      } finally {
        setLoading(false);
        setLoadingPoId(null);
      }
    },
    [onViewPo],
  );

  const normalizeFilterTerm = (value) =>
    (value || "")
      .toString()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

  const getRelaxedTerms = (terms) =>
    terms.flatMap((term) => {
      const relaxed = term.replace(/\s+\d+$/, "").trim();
      return relaxed && relaxed !== term ? [term, relaxed] : [term];
    });

  const filterItemsFromCatalogApi = useCallback(async ({ values, sourceItems }) => {
    try {
      const baseTerms = [...new Set(getRelaxedTerms(values.map(normalizeFilterTerm)).filter(Boolean))];
      console.log("[RC->PO][Chatbot] Filter input values:", baseTerms);
      console.log("[RC->PO][Chatbot] Source RC items count:", sourceItems?.length || 0);

      const payload = baseTerms.map((value) => ({
        partNo: value,
        itemName: value,
      }));

      console.log("[RC->PO][Chatbot] Calling extract API payload:", payload);
      const response = await axios.post(API_ENDPOINT.extract_pdf_view, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const extracted = response?.data?.data;
      console.log("[RC->PO][Chatbot] Extract API response rows:", extracted?.length || 0);
      if (!Array.isArray(extracted) || extracted.length === 0) return [];

      const apiValues = extracted
        .map((row) =>
          (row?.["Part No"] || row?.["Description of Goods"] || "")
            .toString()
            .toLowerCase()
            .trim(),
        )
        .filter(Boolean);

      // Keep filtering strict to user query terms so API over-return doesn't include all items.
      const matchedTerms = baseTerms.filter((term) =>
        apiValues.some((apiValue) => apiValue.includes(term) || term.includes(apiValue)),
      );
      const finalTerms = matchedTerms.length ? matchedTerms : baseTerms;

      const filtered = sourceItems.filter((item) => {
        const part = (item.part_no || item.item_code || item.id || "")
          .toString()
          .toLowerCase()
          .trim();
        const name = (item.item_name || "")
          .toString()
          .toLowerCase()
          .trim();
        return finalTerms.some((term) => part.includes(term) || name.includes(term));
      });

      console.log("[RC->PO][Chatbot] Filtered RC item count:", filtered.length);
      return filtered;
    } catch (error) {
      console.warn("Item catalog API filter failed, using local filter:", error);
      const baseTerms = [...new Set(getRelaxedTerms(values.map(normalizeFilterTerm)).filter(Boolean))];
      const filtered = sourceItems.filter((item) => {
        const part = (item.part_no || item.item_code || item.id || "")
          .toString()
          .toLowerCase()
          .trim();
        const name = (item.item_name || "")
          .toString()
          .toLowerCase()
          .trim();
        return baseTerms.some((term) => part.includes(term) || name.includes(term));
      });
      console.log("[RC->PO][Chatbot] Local fallback filtered RC item count:", filtered.length);
      return filtered;
    }
  }, []);

  useEffect(() => {
    const runChatbotConvertFlow = async () => {
      const chatActionRaw = sessionStorage.getItem("CHAT_ACTION");
      if (!chatActionRaw || rcList.length === 0) return;

      const { action, rc_number_extract, item_values = [] } = JSON.parse(chatActionRaw);
      if (action !== "convert_rc_to_po") return;

      console.log("[RC->PO][Chatbot] CHAT_ACTION:", {
        action,
        rc_number_extract,
        item_values,
      });

      onActiveTabChange(1);

      const rawRcExtract = (rc_number_extract || "").toString().trim();
      const firstItemRaw = (item_values?.[0] || "").toString().trim();
      const safeCode = rawRcExtract.toLowerCase();

      let matchedRC = rcList.find((item) => item.rc_number?.toLowerCase() === safeCode);

      if (!matchedRC && rawRcExtract) {
        matchedRC = rcList.find((item) =>
          safeCode.includes((item.rc_number || "").toLowerCase()),
        );
      }

      if (!matchedRC && firstItemRaw) {
        const firstLower = firstItemRaw.toLowerCase();
        matchedRC = rcList.find((item) =>
          firstLower.includes((item.rc_number || "").toLowerCase()),
        );
      }

      if (!matchedRC) {
        console.warn("RC not found:", safeCode || firstItemRaw);
        return;
      }

      console.log("[RC->PO][Chatbot] Matched RC:", matchedRC?.rc_number);

      const sourceItems = Array.isArray(matchedRC?.items) ? matchedRC.items : [];
      if (!sourceItems.length) {
        console.warn("No RC items available for chatbot conversion");
        return;
      }

      const values = item_values
        .flatMap((v) => v?.toString().split(",") || [])
        .map((v) => v.toLowerCase().trim())
        .filter(Boolean);

      const rcNumberEscaped = (matchedRC.rc_number || "").replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      const derivedFromRcExtract = rawRcExtract
        .replace(new RegExp(rcNumberEscaped, "i"), "")
        .trim()
        .toLowerCase();

      const derivedFromItems = item_values
        .map((v) => (v || "").toString())
        .map((v) => v.replace(new RegExp(rcNumberEscaped, "i"), "").trim().toLowerCase())
        .filter(Boolean);

      const rawEffectiveValues = [
        ...values,
        ...(derivedFromRcExtract ? [derivedFromRcExtract] : []),
        ...derivedFromItems,
      ].filter(Boolean);

      // Expand phrases like "1kva ups 7" to also include "1kva ups", then de-duplicate.
      const expandedValues = rawEffectiveValues.flatMap((value) => {
        const compact = value.replace(/\s+/g, " ").trim();
        const strippedTrailingNumber = compact.replace(/\s+\d+$/, "").trim();
        return strippedTrailingNumber && strippedTrailingNumber !== compact
          ? [compact, strippedTrailingNumber]
          : [compact];
      });

      const effectiveValues = [...new Set(expandedValues)];

      console.log("[RC->PO][Chatbot] Effective search values:", effectiveValues);

      const filteredItems = await filterItemsFromCatalogApi({
        values: effectiveValues,
        sourceItems,
      });

      if (!filteredItems.length) {
        console.warn("No matching line items found for chatbot conversion");
        return;
      }

      console.log(
        "[RC->PO][Chatbot] Navigating to PO view with filtered items:",
        filteredItems,
      );

      onActiveTabChange(0);
      onViewPo({
        ...matchedRC,
        items: filteredItems,
        chatbotConverted: true,
        source: "item_catalog",
      });
      sessionStorage.removeItem("CHAT_ACTION");
    };

    runChatbotConvertFlow();
  }, [rcList, onActiveTabChange, onViewPo, filterItemsFromCatalogApi]);

  const rateContractTrackerRecords = rcList.filter(
    (item) => item?.no_filter !== true || isPoAutomationRecord(item),
  );
  const itemCatalogTrackerRecords = rcList.filter(
    (item) => item?.no_filter === true && !isPoAutomationRecord(item),
  );

  const filteredRc = rateContractTrackerRecords.filter(
    (rc) =>
      rc.rc_number?.toLowerCase().includes(search.toLowerCase()) ||
      rc.buyer?.toLowerCase().includes(search.toLowerCase()) ||
      getVendorName(rc).toLowerCase().includes(search.toLowerCase()),
  );

  const paginatedRc = filteredRc.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const filteredItemCatalogRecords = itemCatalogTrackerRecords.filter(
    (item) =>
      item?.rc_number?.toLowerCase().includes(extractedSearch.toLowerCase()) ||
      item?.buyer?.toLowerCase().includes(extractedSearch.toLowerCase()) ||
      item?.start_date?.toLowerCase().includes(extractedSearch.toLowerCase()) ||
      getVendorName(item).toLowerCase().includes(extractedSearch.toLowerCase()),
  );

  const mapRcItemsToModalRecord = (record) => ({
    data: (record?.items || []).map((item, index) => ({
      "Sr. No.": index + 1,
      "Part No": item?.part_no || item?.item_code || "-",
      "Description of Goods": item?.item_name || "-",
      "HSN Code": item?.hsn_code || "-",
      "Qty.": Number(item?.quantity || 0),
      UOM: item?.uom || "-",
      "IGST Rate": item?.igst_rate || "-",
      "SGST Rate": item?.sgst_rate || "-",
      Rate: Number(item?.rate || 0),
      _row_total:
        Number(item?.total_price || 0) ||
        Number(item?.rate || 0) * Number(item?.quantity || 0),
    })),
  });

  const handleOpenLineItems = (record) => {
    setSelectedExtractedRecord(record);
    setLineItemsOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <HeaderTitle titleText="Rate Contract" />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            sx={enterprisePrimaryButtonSx}
            onClick={onExtractData}
          >
            Item Catalog
          </Button>

          <Button
            variant="contained"
            size="small"
            sx={enterprisePrimaryButtonSx}
            onClick={onAddRc}
          >
            Upload RC
          </Button>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
        <Tabs value={activeTab} onChange={(_, value) => onActiveTabChange(value)}>
          <Tab
            label="Rate Contract Tracker"
            sx={{ textTransform: "none", fontSize: "16px", fontFamily: "gilroyBold" }}
          />
          <Tab
            label="Item Catalog Tracker"
            sx={{ textTransform: "none", fontSize: "16px", fontFamily: "gilroyBold" }}
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          <Box sx={{ flex: 1, mt: "20px" }}>
            <SearchBar
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
          </Box>

          <Box sx={{ mt: "20px", width: "100%", overflowX: "auto" }}>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      "RC-PO Code",
                      "RC Number",
                      
                      "Supplier Name",
                      "Start Date",
                      "End Date",
                      "Created Date",
                      "Total Contract Value",
                      "Action",
                    ].map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          whiteSpace:"nowrap",
                          fontWeight: 600,
                          fontFamily: "GilroySemiBold",
                          backgroundColor: "#e0e9ee",
                          fontSize: "14px",
                          borderBottom: "1px solid #e2e8f0",
                          ...(index === 0 ? stickyStartHeaderSx : {}),
                          ...(index === 7 ? stickyEndHeaderSx : {}),
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loadingRc && filteredRc.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRc.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: "white",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" ,whiteSpace:"nowrap", ...stickyStartCellSx }}>
                        {item.rc_number||"-"}
                      </TableCell>
                       <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" ,whiteSpace:"nowrap"}}>
                        {item.purchase_order_number || "-"}
                      </TableCell>
                       {/* <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                        {item.purchase_order_date || "-"}
                      </TableCell> */}
                      <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px",whiteSpace:"nowrap" }}>
                        {getVendorName(item)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" ,whiteSpace:"nowrap"}}>
                        {item.start_date}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px",whiteSpace:"nowrap" }}>
                        {item.end_date || "-"}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px",whiteSpace:"nowrap" }}>
                        {formatDate(item.created_on)}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#075b97",
                          fontFamily: "gilroyBold",
                          fontSize: "14px",
                        }}
                      >
                        Rs. {Number(item.contract_value || 0).toLocaleString("en-IN")}
                      </TableCell>

                      <TableCell sx={stickyEndCellSx}>
                        <Button
                          variant="outlined"
                          size="medium"
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 1.5,
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            opacity: loadingPoId === item.rc_token ? 0.6 : 1,
                          }}
                          disabled={loadingPoId === item.rc_token}
                          onClick={() => handleCreatePO(item)}
                        >
                          {loadingPoId === item.rc_token ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            "Create PO"
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <PaginantionComponent
              pageNumber={page}
              setPageNumber={(value) => {
                if (value === "") return;
                const parsed = parseInt(value, 10);
                if (!Number.isNaN(parsed)) {
                  setPage(parsed);
                }
              }}
              currentPage={page}
              itemsPerPage={filteredRc.length}
              totalItems={
                filteredRc.length > 0
                  ? Math.ceil(filteredRc.length / rowsPerPage)
                  : 0
              }
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onRowsPerPageChange={(event) => {
                const newRows = parseInt(event.target.value, 10);
                setRowsPerPage(Number.isNaN(newRows) ? rowsPerPage : newRows);
                setPage(1);
              }}
              previouPageClick={() => setPage((prev) => Math.max(1, prev - 1))}
              nextPageClick={() =>
                setPage((prev) =>
                  filteredRc.length > 0
                    ? Math.min(Math.ceil(filteredRc.length / rowsPerPage), prev + 1)
                    : 1,
                )
              }
              disablePrevious={page <= 1}
              disableNext={
                filteredRc.length === 0 ||
                page >= Math.ceil(filteredRc.length / rowsPerPage)
              }
            />
          </Box>
        </>
      )}

      {activeTab === 1 && (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Box sx={{ flex: 1, mb: 2 }}>
            <SearchBar
              value={extractedSearch}
              onChange={(value) => {
                setExtractedSearch(value);
              }}
            />
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Table stickyHeader sx={{ whiteSpace: "nowrap" }}>
              <TableHead>
                <TableRow>
                  {[
                    "RC-PO Code",
                    "RC Number",
                    // "Order Date",
                    "Supplier Name",
                    "Created Date",
                    "Grand Total",
                    "Action",
                  ].map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        fontWeight: 600,
                        fontFamily: "GilroySemiBold",
                        backgroundColor: "#e0e9ee",
                        fontSize: "14px",
                        borderBottom: "1px solid #e2e8f0",
                        whiteSpace: "nowrap",
                        ...(index === 0 ? stickyStartHeaderSx : {}),
                        ...(index === 5 ? stickyEndHeaderSx : {}),
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loadingRc && filteredItemCatalogRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItemCatalogRecords.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px", whiteSpace: "nowrap", ...stickyStartCellSx }}>
                      {item?.rc_number || "-"}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px", whiteSpace: "nowrap" }}>
                      {item?.purchase_order_number || "-"}
                    </TableCell>
                    {/* <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px", whiteSpace: "nowrap" }}>
                      {item?.purchase_order_date || "-"}
                    </TableCell> */}
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px", whiteSpace: "nowrap" }}>
                      {getVendorName(item)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px", whiteSpace: "nowrap" }}>
                      {formatDate(item?.created_on || "-")}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#075b97",
                        fontFamily: "gilroyBold",
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Rs. {Number(item?.contract_value || 0).toLocaleString("en-IN")}
                    </TableCell>

                    <TableCell sx={stickyEndCellSx}>
                      <Button
                        variant="outlined"
                        size="medium"
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 1.5,
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => handleOpenLineItems(mapRcItemsToModalRecord(item))}
                      >
                        View Items
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
                )}

                {!loadingRc && filteredItemCatalogRecords.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        fontFamily: "GilroyMedium",
                        fontSize: "14px",
                        textAlign: "center",
                      }}
                    >
                      No item catalog records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <ExtractedLineItemsModal
        open={lineItemsOpen}
        onClose={() => setLineItemsOpen(false)}
        record={selectedExtractedRecord}
      />
    </Box>
  );
};

export default RcListView;
